import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticsBloc, AnalyticsData, AnalyticsPageName } from '@kanalytics';
import { AuthenticationBloc } from '@kp/auth/authentication.bloc';
import { DialogueBloc } from '@kp/dialogue/dialogue.bloc';
import { PulseSurveyBloc } from '@kp/pulse-survey/pulse-survey.bloc';
import { Constants } from '@kp/shared/constants.service';
import { GlobalElementsService } from '@kp/shared/services/global-elements.service';
import { DataStoreService, ThemeService, productDisplayName } from '@kservice';
import { Status } from '@ktypes/models';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'kp-site-basic-logo-header',
  templateUrl: './site-basic-logo-header.component.html',
  styleUrls: ['./site-basic-logo-header.component.scss'],
})
export class SiteBasicLogoHeaderComponent implements OnInit, OnDestroy {
  analyticsData: AnalyticsData;
  showHelpModal = false;
  showFeedbackModal = false;
  routesNotToNavigateFrom = [
    'dialogue',
    'resources',
    'privacy-pledge',
    'pulse_survey',
    'question_set',
    'report',
    'thank_you',
  ];
  productName = productDisplayName;
  constants = Constants;

  private _destroy$ = new Subject<void>();

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _authBloc: AuthenticationBloc,
    private _analyticsBloc: AnalyticsBloc,
    public dataStoreService: DataStoreService,
    private _dialogueBloc: DialogueBloc,
    @Inject(DOCUMENT) private _document: Document,
    public globalElementsService: GlobalElementsService,
    private _pulseSurveyBloc: PulseSurveyBloc,
    private _renderer: Renderer2,
    private _router: Router,
    public themeService: ThemeService
  ) {}

  ngOnInit() {
    combineLatest([this._pulseSurveyBloc.pulseSurveySubject$, this._dialogueBloc.dialogue$])
      .pipe(takeUntil(this._destroy$))
      .subscribe(([pulseSurveyStatus, dialogueStatus]) => {
        if (dialogueStatus?.status === Status.done && dialogueStatus?.data) {
          this.analyticsData = {
            page: AnalyticsPageName.dialogue,
            meta: {
              logicKey: dialogueStatus.data?.logicKey,
              dialogueId: dialogueStatus.data?.id,
              questionId: dialogueStatus.data?.dialogueBlock?.question?.id,
            },
          };
        } else if (pulseSurveyStatus?.status === Status.done && pulseSurveyStatus?.data) {
          this.analyticsData = {
            page: AnalyticsPageName.pulseSurveyIntroScreen,
            meta: {
              surveyId: pulseSurveyStatus?.data?.id,
            },
          };
        }
      });
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  isHelpModalOpen() {
    this._renderer.removeClass(this._document.documentElement, 'no-scroll');
    this.showHelpModal = false;
  }

  isFeedbackModalOpen() {
    this._renderer.removeClass(this._document.documentElement, 'no-scroll');
    this.showFeedbackModal = false;
  }

  letLogoNavigate() {
    const url = this._activatedRoute.snapshot.firstChild.url.toString();
    return this._authBloc.isLoggedIn() || !this.urlHasMatch(this.routesNotToNavigateFrom, url);
  }

  logoNavigate() {
    if (this.letLogoNavigate()) {
      void this._router.navigate(['/']);
    }
  }

  urlHasMatch(nonNavigableRoutes, url) {
    if (Array.isArray(nonNavigableRoutes) && url) {
      return nonNavigableRoutes.some((nonNavigableRoute) => url.match(RegExp(nonNavigableRoute)));
    }
    return false;
  }
}
